import { FC, useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from 'components/lib/utils';

import {
  MONTH_CELL_BASE_CLASSES,
  MONTH_CELL_CURRENT_CLASSES,
  MONTH_CELL_DISABLED_CLASSES,
  MONTH_CELL_HOVER_CLASSES,
  MONTH_CELL_SELECTED_CLASSES
} from './MonthPicker.consts';
import { IMonthPickerProps } from './MonthPicker.types';
import {
  canNavigateToYear,
  formatMonthShort,
  getMonthCellsForYear,
  isMonthDisabled,
  isSameMonth
} from './MonthPicker.utils';
import { YearNavButton } from './YearNavButton';

interface IMonthCellProps {
  candidateMonth: Date;
  selectedMonth: Date;
  todayMonth: Date;
  minSelectableMonth?: Date;
  maxSelectableMonth?: Date;
  onSelect: (firstOfMonth: Date) => void;
}

const MonthCell: FC<IMonthCellProps> = ({
  candidateMonth,
  selectedMonth,
  todayMonth,
  minSelectableMonth,
  maxSelectableMonth,
  onSelect
}) => {
  const isDisabled = isMonthDisabled({
    candidateMonth,
    minSelectableMonth,
    maxSelectableMonth
  });
  const isSelected = isSameMonth(candidateMonth, selectedMonth);
  const isCurrentMonth = isSameMonth(candidateMonth, todayMonth);

  const handleClick = () => {
    if (isDisabled) {
      return;
    }
    onSelect(candidateMonth);
  };

  return (
    <button
      type="button"
      data-testid={`month-cell-${candidateMonth.getMonth()}`}
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        MONTH_CELL_BASE_CLASSES,
        !isDisabled && MONTH_CELL_HOVER_CLASSES,
        isCurrentMonth && !isSelected && MONTH_CELL_CURRENT_CLASSES,
        isSelected && MONTH_CELL_SELECTED_CLASSES,
        isDisabled && MONTH_CELL_DISABLED_CLASSES
      )}
    >
      {formatMonthShort(candidateMonth)}
    </button>
  );
};

const MonthPicker: FC<IMonthPickerProps> = ({
  value,
  onChange,
  minSelectableMonth,
  maxSelectableMonth,
  dataTestId,
  className
}) => {
  const [viewYear, setViewYear] = useState<number>(value.getFullYear());

  const todayMonth = new Date();

  const monthCellsForViewYear = useMemo(
    () => getMonthCellsForYear(viewYear),
    [viewYear]
  );

  const canNavigateToPreviousYear = canNavigateToYear({
    targetYear: viewYear - 1,
    minSelectableMonth,
    maxSelectableMonth
  });
  const canNavigateToNextYear = canNavigateToYear({
    targetYear: viewYear + 1,
    minSelectableMonth,
    maxSelectableMonth
  });

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'border border-base-border rounded-md p-3 bg-base-popover',
        className
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <YearNavButton
          testId="month-picker-previous-year"
          ariaLabel="Previous year"
          icon={<ChevronLeft className="h-4 w-4" />}
          isEnabled={canNavigateToPreviousYear}
          onNavigate={() => setViewYear((currentViewYear) => currentViewYear - 1)}
        />
        <span
          data-testid="month-picker-view-year"
          className="text-sm font-medium text-base-foreground"
        >
          {viewYear}
        </span>
        <YearNavButton
          testId="month-picker-next-year"
          ariaLabel="Next year"
          icon={<ChevronRight className="h-4 w-4" />}
          isEnabled={canNavigateToNextYear}
          onNavigate={() => setViewYear((currentViewYear) => currentViewYear + 1)}
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {monthCellsForViewYear.map((candidateMonth) => (
          <MonthCell
            key={candidateMonth.getMonth()}
            candidateMonth={candidateMonth}
            selectedMonth={value}
            todayMonth={todayMonth}
            minSelectableMonth={minSelectableMonth}
            maxSelectableMonth={maxSelectableMonth}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  );
};

export default MonthPicker;
